import { useEffect, useLayoutEffect } from "react";
import { Editor, isNodeSelection, posToDOMRect } from "@tiptap/core";

import { usePoppable } from "@/hooks/usePoppable";

type Props = {
  editor: Editor;
  open: boolean;
  children: React.ReactNode;
};

export const BubbleMenu: React.FC<Props> = ({
  editor,
  open,
  children,
}: Props) => {
  const { refs, style, isPoppableVisible, setIsPoppableVisible } = usePoppable({
    placement: "top",
    options: { offset: 8 },
  });

  // The below comes from here:
  // https://github.com/ueberdosis/tiptap/blob/16bec4e9d0c99feded855b261edb6e0d3f0bad21/packages/extension-bubble-menu/src/bubble-menu-plugin.ts#L183-L193
  const { ranges } = editor.state.selection;
  const from = Math.min(...ranges.map((range) => range.$from.pos));
  const to = Math.max(...ranges.map((range) => range.$to.pos));

  useLayoutEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect: () => {
        if (isNodeSelection(editor.state.selection)) {
          const node = editor.view.nodeDOM(from) as HTMLElement;

          if (node) {
            return node.getBoundingClientRect();
          }
        }

        return posToDOMRect(editor.view, from, to);
      },
    });
  }, [
    editor.state.selection,
    from,
    to,
    editor.view,
    refs.setPositionReference,
  ]);

  useEffect(() => {
    if (from === to) {
      setIsPoppableVisible(false);
    } else if (from !== to) {
      setIsPoppableVisible(true);
    }
  }, [from, to, setIsPoppableVisible]);

  // Prop "open" overrides internal from/to logic.
  useEffect(() => {
    if (from !== to && !isPoppableVisible) {
      setIsPoppableVisible(open);
    }
  }, [isPoppableVisible, from, to, open, setIsPoppableVisible]);

  useEffect(() => {
    setIsPoppableVisible(false);
  }, []); // eslint-disable-line

  return isPoppableVisible ? (
    <div ref={refs.setFloating} style={{ ...style }}>
      {children}
    </div>
  ) : null;
};
